/**
 * One user's seen area on one slide: loads the live bitmap and the tissue file, keeps newly seen tiles
 * in a localStorage buffer and flushes them to the API (OR-merge on the server, so resends are harmless).
 */
import {AuthHeader} from 'configs/auth.token';
import {get, post} from '../../../../helper/request';
import {getStateAwareUrl, isCloud, joinPath} from '../../../../utils/utils';
import {getBaseSlidePath} from '../../../../utils/slide_utils';
import {
    emptyBits,
    getLevelLayout,
    gridSignature,
    levelKey,
    MAGIC_SEEN,
    MAGIC_TISSUE,
    orBits,
    parseSeenAreaFile,
    setBit,
    STITCHED,
    TILED,
} from './seenAreaFormat';

export const FLUSH_INTERVAL_MS = 15000;
const PENDING_PREFIX = 'osd-seen:';
const PENDING_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Browsers drop keepalive requests whose body is over 64 KiB. */
const KEEPALIVE_MAX_BYTES = 60000;

export const SESSION_STATUS = {IDLE: 'idle', LOADING: 'loading', READY: 'ready', DISABLED: 'disabled'};

const seenAreaApi = (slideId) => `/api/slide/${slideId}/seen_area/`;

export const getSeenAreaTissueFilename = (morphleId) => `${morphleId}_seen_area_tissue.bin`;

export const pendingStorageKey = (userId, slideId, signature) => `${PENDING_PREFIX}${userId}:${slideId}:${signature}`;

const readStored = (key) => {
    try {
        const parsed = JSON.parse(localStorage.getItem(key) || 'null');
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
        return null;
    }
};

/** Drops buffers nobody flushed for 30 days (slide gone, user changed machine). */
const pruneStalePending = (now = Date.now()) => {
    try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (!key?.startsWith(PENDING_PREFIX)) continue;
            const stored = readStored(key);
            if (!stored || !(now - (stored.t || 0) < PENDING_MAX_AGE_MS)) localStorage.removeItem(key);
        }
    } catch (e) { /* storage unavailable */ }
};

/** {stitched: {L: Set}, tiled: {T: Set}} helpers; JSON form uses arrays. */
const emptyPending = () => ({[STITCHED]: {}, [TILED]: {}});

const mergePending = (target, source) => {
    for (const kind of [STITCHED, TILED]) {
        for (const level in source?.[kind] || {}) {
            const set = target[kind][level] || (target[kind][level] = new Set());
            for (const idx of source[kind][level]) if (Number.isInteger(idx)) set.add(idx);
        }
    }
    return target;
};

const subtractPending = (target, acked) => {
    for (const kind of [STITCHED, TILED]) {
        for (const level in acked?.[kind] || {}) {
            const set = target[kind][level];
            if (!set) continue;
            for (const idx of acked[kind][level]) set.delete(idx);
            if (!set.size) delete target[kind][level];
        }
    }
    return target;
};

const pendingToJson = (pending) => {
    const out = emptyPending();
    for (const kind of [STITCHED, TILED]) {
        for (const level in pending[kind]) out[kind][level] = [...pending[kind][level]];
    }
    return out;
};

const isPendingEmpty = (pending) => [STITCHED, TILED].every(kind => Object.keys(pending[kind]).length === 0);

export class SeenAreaSession {
    constructor(slide, onChange = () => {}) {
        this.slide = slide;
        this.onChange = onChange;
        this.status = SESSION_STATUS.IDLE;
        this.grid = null;
        this.layout = null;
        this.signature = null;
        this.userId = null;
        this.seenAreaId = null;
        this.seen = null;
        this.tissue = null;
        this.version = 0;
        this._pending = emptyPending();
        this._inflight = null;
        this._timer = null;
        this._disposed = false;
    }

    start = async () => {
        if (isCloud() || !this.slide?.id) {
            this.status = SESSION_STATUS.DISABLED;
            return;
        }
        this.status = SESSION_STATUS.LOADING;
        let info;
        try {
            info = await get(seenAreaApi(this.slide.id), true);
        } catch (e) {
            // 403 guest link / anonymous, 404 cloud, 409 no stitched grid: nothing is recorded.
            this.status = SESSION_STATUS.DISABLED;
            this._emit();
            return;
        }
        if (this._disposed || !info?.grid) return;

        this.grid = info.grid;
        this.layout = getLevelLayout(info.grid);
        this.signature = info.grid_signature || gridSignature(info.grid);
        this.userId = info.user_id;
        this.seenAreaId = info.seen_area_id;
        this.seen = emptyBits(this.layout);
        this.tissue = null;

        pruneStalePending();
        mergePending(this._pending, readStored(this._storageKey()));
        this._applyPendingToSeen();

        const [own] = await Promise.all([
            this.seenAreaId ? this.fetchBitmap(['meta', 'seen_area', `${this.seenAreaId}.bin`], MAGIC_SEEN) : null,
            this.loadTissue(),
        ]);
        if (this._disposed) return;
        if (own) orBits(this.seen, own, this.layout);

        this.status = SESSION_STATUS.READY;
        this._timer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
        window.addEventListener('pagehide', this._onPageHide);
        document.addEventListener('visibilitychange', this._onVisibilityChange);
        this._changed();
        this.flush();
    };

    _storageKey = () => pendingStorageKey(this.userId, this.slide.id, this.signature);

    _emit = () => {
        if (!this._disposed) this.onChange(this);
    };

    _changed = () => {
        this.version += 1;
        this._emit();
    };

    _applyPendingToSeen = () => {
        for (const kind of [STITCHED, TILED]) {
            for (const level in this._pending[kind]) {
                const entry = this.layout.byKey[levelKey(kind, level)];
                if (entry) this._pending[kind][level].forEach(idx => setBit(this.seen, entry, idx));
            }
        }
    };

    /** Merges with what other tabs on the same slide buffered under the same key. */
    _savePending = (acked = null) => {
        const key = this._storageKey();
        try {
            const stored = mergePending(emptyPending(), readStored(key));
            if (acked) subtractPending(stored, acked);
            mergePending(stored, pendingToJson(this._pending));
            if (isPendingEmpty(stored)) localStorage.removeItem(key);
            else localStorage.setItem(key, JSON.stringify({t: Date.now(), ...pendingToJson(stored)}));
        } catch (e) { /* quota or storage disabled: the in-memory buffer still flushes */ }
    };

    _dropPending = () => {
        this._pending = emptyPending();
        try {
            localStorage.removeItem(this._storageKey());
        } catch (e) { /* ignore */ }
    };

    /** Relative to the slide folder, e.g. ['meta', 'seen_area', 'x.bin']; null when missing or for another grid. */
    fetchBitmap = async (parts, magic, bypassCache = false) => {
        try {
            let url = getStateAwareUrl(joinPath(getBaseSlidePath(this.slide), ...parts));
            if (bypassCache) url += `?v=${Date.now()}`;
            const response = await fetch(url, {cache: 'no-store'});
            if (!response.ok) return null;
            const parsed = parseSeenAreaFile(await response.arrayBuffer());
            if (!parsed || parsed.magic !== magic || gridSignature(parsed.grid) !== this.signature) return null;
            return parsed.bytes;
        } catch (e) {
            return null;
        }
    };

    /** Re-read after an on-demand stitch-manifest build, which also writes the tissue file. */
    loadTissue = async (bypassCache = false) => {
        if (!this.slide?.morphle_id || !this.layout) return null;
        const tissue = await this.fetchBitmap(['res', getSeenAreaTissueFilename(this.slide.morphle_id)], MAGIC_TISSUE, bypassCache);
        if (this._disposed) return null;
        this.tissue = tissue;
        if (this.status === SESSION_STATUS.READY) this._changed();
        return tissue;
    };

    /** `keys`: [[kind, level, idx], ...] from the tracker. */
    markSeen = (keys) => {
        if (this.status !== SESSION_STATUS.READY || !keys.length) return;
        let added = false;
        for (const [kind, level, idx] of keys) {
            const entry = this.layout.byKey[levelKey(kind, level)];
            if (!entry || !setBit(this.seen, entry, idx)) continue;
            const set = this._pending[kind][level] || (this._pending[kind][level] = new Set());
            set.add(idx);
            added = true;
        }
        if (!added) return;
        this._savePending();
        this._changed();
    };

    flush = ({keepalive = false} = {}) => {
        if (this.status !== SESSION_STATUS.READY || this._inflight || isPendingEmpty(this._pending)) {
            return this._inflight || Promise.resolve();
        }
        const snapshot = pendingToJson(this._pending);
        const body = JSON.stringify({grid_signature: this.signature, ...snapshot});
        this._inflight = fetch(getStateAwareUrl(seenAreaApi(this.slide.id)), {
            method: 'POST',
            keepalive: keepalive && body.length < KEEPALIVE_MAX_BYTES,
            headers: {'Content-Type': 'application/json', Authorization: AuthHeader()},
            body,
        }).then(async response => {
            if (response.ok) {
                const data = await response.json().catch(() => null);
                if (data?.seen_area_id) this.seenAreaId = data.seen_area_id;
                subtractPending(this._pending, snapshot);
                this._savePending(snapshot);
                return;
            }
            const data = response.status === 409 ? await response.json().catch(() => null) : null;
            if (data?.grid_signature && data.grid_signature !== this.signature) {
                // Restitched since the grid was fetched: the buffered indices belong to the old grid.
                this._dropPending();
                this._restart();
            }
        }).catch(() => {
            // Offline or server error: the buffer stays and is retried.
        }).finally(() => {
            this._inflight = null;
        });
        return this._inflight;
    };

    _restart = () => {
        if (this._disposed) return;
        this._stopTimers();
        this.status = SESSION_STATUS.LOADING;
        this._emit();
        this.start();
    };

    /** H16: the server rotates the live file; buffered and in-flight tiles are discarded. */
    reset = async () => {
        if (this.status !== SESSION_STATUS.READY) return false;
        await this._inflight;
        this._dropPending();
        const {reset} = await post(`${seenAreaApi(this.slide.id)}reset/`, {});
        this.seen = emptyBits(this.layout);
        this._changed();
        return reset;
    };

    /** Superusers: [{user_id, username, seen_area_id}] with a live file on this slide. */
    listUsers = () => get(`${seenAreaApi(this.slide.id)}users/`, true);

    loadUserBits = (seenAreaId) => this.fetchBitmap(['meta', 'seen_area', `${seenAreaId}.bin`], MAGIC_SEEN, true);

    _onPageHide = () => this.flush({keepalive: true});

    _onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') this.flush({keepalive: true});
    };

    _stopTimers = () => {
        clearInterval(this._timer);
        this._timer = null;
        window.removeEventListener('pagehide', this._onPageHide);
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
    };

    /** Slide change or unmount: last flush with keepalive; leftovers stay buffered for the next load. */
    dispose = () => {
        this.flush({keepalive: true});
        this._stopTimers();
        this._disposed = true;
    };
}
